import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { segmentTypes, segmentCriteria } from '../data/mockSegments';

const SegmentBuilder = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: '',
    color: '#173131',
    criteria: {}
  });
  const [errors, setErrors] = useState({});
  const [estimatedSize, setEstimatedSize] = useState(0);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });

    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }

    // Simulate audience size calculation
    if (field === 'type' || field === 'name') {
      const randomSize = Math.floor(Math.random() * 2000) + 100;
      setEstimatedSize(randomSize);
    }
  };

  const handleCriteriaChange = (criteriaKey, value) => {
    setFormData({
      ...formData,
      criteria: {
        ...formData.criteria,
        [criteriaKey]: value
      }
    });

    // Update estimated size when criteria changes
    const randomSize = Math.floor(Math.random() * 2000) + 100;
    setEstimatedSize(randomSize);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Segment name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.type) newErrors.type = 'Segment type is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (validateForm()) {
      console.log('Segment created:', { ...formData, estimatedSize });
      alert('Segment created successfully!');
      navigate('/marketing/segments');
    }
  };

  const handleCancel = () => {
    navigate('/marketing/segments');
  };

  const renderCriteriaFields = () => {
    if (!formData.type || !segmentCriteria[formData.type]) return null;

    const criteria = segmentCriteria[formData.type];

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Segment Criteria
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(criteria).map(([key, config]) => (
            <Grid item xs={12} md={6} key={key}>
              {config.type === 'multiselect' && (
                <FormControl fullWidth>
                  <InputLabel>{config.label}</InputLabel>
                  <Select
                    multiple
                    value={formData.criteria[key] || []}
                    onChange={(e) => handleCriteriaChange(key, e.target.value)}
                    input={<OutlinedInput label={config.label} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {config.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {config.type === 'range' && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {config.label}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Min"
                        size="small"
                        value={formData.criteria[key]?.min || ''}
                        onChange={(e) => handleCriteriaChange(key, {
                          ...formData.criteria[key],
                          min: parseInt(e.target.value) || 0
                        })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Max"
                        size="small"
                        value={formData.criteria[key]?.max || ''}
                        onChange={(e) => handleCriteriaChange(key, {
                          ...formData.criteria[key],
                          max: parseInt(e.target.value) || 0
                        })}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {config.type === 'select' && (
                <TextField
                  fullWidth
                  select
                  label={config.label}
                  value={formData.criteria[key] || ''}
                  onChange={(e) => handleCriteriaChange(key, e.target.value)}
                >
                  {config.options.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/marketing/segments')} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight={600}>
              Create Customer Segment
            </Typography>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <CardContent>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Segment Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Segment Type"
                value={formData.type}
                onChange={handleChange('type')}
                error={!!errors.type}
                helperText={errors.type}
                required
              >
                {segmentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {type.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
                error={!!errors.description}
                helperText={errors.description || 'Describe who this segment targets'}
                required
              />
            </Grid>

            {renderCriteriaFields()}

            {estimatedSize > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Audience Preview
                  </Typography>
                  <Typography variant="body1">
                    Estimated audience size: <strong>{estimatedSize.toLocaleString()} customers</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on current criteria and historical data
                  </Typography>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(23, 49, 49, 0.3)'
                    }
                  }}
                >
                  Create Segment
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SegmentBuilder;